import { type UseQueryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';

import { useQueryStateAppContext } from '@/packages/query-state/shared/context/appContext';
import { getCancellationTokenFromSignal } from '@/packages/utils/getCancellationTokenFromSignal';

import {
  type TGetBannerEnrichmentsRequest,
  type TGetBannerEnrichmentsResponse_1,
} from '@/repositories/generated/shared/ad-banner/v1/get-banner-enrichments';

import { type QueryError, type QueryKey, getQueryKey } from './internal/config';
import { fetchGetBannerEnrichmentsService } from './internal/fetchService';

type UseGetBannerEnrichmentsQueryOptions<TData> = Omit<
  UseQueryOptions<TGetBannerEnrichmentsResponse_1, QueryError, TData, QueryKey>,
  'queryFn' | 'queryKey'
>;

type UseGetBannerEnrichmentsQueryParams = TGetBannerEnrichmentsRequest;

export function useGetBannerEnrichmentsQuery<TData = TGetBannerEnrichmentsResponse_1>(
  parameters: UseGetBannerEnrichmentsQueryParams,
  options?: UseGetBannerEnrichmentsQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const applicationContext = useQueryStateAppContext();

  return useQuery<TGetBannerEnrichmentsResponse_1, QueryError, TData, QueryKey>({
    queryFn: ({ signal }) =>
      fetchGetBannerEnrichmentsService({
        applicationContext,
        cancellationToken: getCancellationTokenFromSignal(signal),
        parameters,
      }),
    queryKey: getQueryKey(parameters),
    ...options,
  });
}
